/* ------------------------------------------------------------------
   Precious Jewels wishlist.
   Stores saved product handles in this browser. No app, no vendor.
   Settings and strings arrive on window.PJWL from pj-wishlist-head.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var KEY = 'pj_wishlist';
  var cfg = window.PJWL || {};
  var strings = cfg.strings || {};

  /* ---------- storage ---------- */

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(function (i) { return i && i.h; }) : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode, quota, blocked site data — the page still works */
    }
    document.dispatchEvent(new CustomEvent('pj:wishlist:change', { detail: { count: list.length } }));
    schedulePush();
  }

  function has(handle) {
    return read().some(function (i) { return i.h === handle; });
  }

  function toggle(handle, id) {
    var list = read();
    var at = -1;
    for (var i = 0; i < list.length; i++) { if (list[i].h === handle) { at = i; break; } }
    if (at > -1) {
      list.splice(at, 1);
    } else {
      list.unshift({ h: handle, id: id || null, t: Date.now() });
    }
    write(list);
    return at === -1;
  }

  function remove(handle) {
    write(read().filter(function (i) { return i.h !== handle; }));
  }

  /* ---------- account sync ----------
     Signed out, the list lives in this browser and nowhere else. Signed in, it is
     mirrored onto the customer record through the Shopify app proxy, so it follows
     the shopper to their phone. Shopify signs the proxy request and appends the
     customer id, so the server never trusts an identity this script sends.
     Every failure here is silent on purpose: the browser copy still works.       */

  var sync = cfg.sync || {};
  var ACCOUNT_KEY = 'pj_wishlist_account';
  var pushTimer = null;
  var applying = false;

  function marker(value) {
    try {
      if (value === undefined) return window.localStorage.getItem(ACCOUNT_KEY);
      if (value === null) window.localStorage.removeItem(ACCOUNT_KEY);
      else window.localStorage.setItem(ACCOUNT_KEY, String(value));
    } catch (e) {}
    return null;
  }

  function push() {
    if (!sync.on || !sync.url) return;
    try {
      fetch(sync.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ list: read() }),
        credentials: 'same-origin',
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }

  /* write() calls this on every change; `applying` keeps the server's own list
     from being echoed straight back at it. */
  function schedulePush() {
    if (!sync.on || applying) return;
    clearTimeout(pushTimer);
    pushTimer = setTimeout(push, 700);
  }

  function apply(list) {
    applying = true;
    write(list);
    applying = false;
    syncButtons();
    syncCount();
    renderPage();
  }

  function newest(lists) {
    var byHandle = {};
    lists.forEach(function (list) {
      (list || []).forEach(function (i) {
        if (!i || !i.h) return;
        var prev = byHandle[i.h];
        if (!prev || (i.t || 0) > (prev.t || 0)) byHandle[i.h] = i;
      });
    });
    return Object.keys(byHandle)
      .map(function (h) { return byHandle[h]; })
      .sort(function (a, b) { return (b.t || 0) - (a.t || 0); });
  }

  function pull() {
    /* Signed out with a marker still set means the shopper has just left: the list
       on screen belongs to their account, so it should not sit here waiting for
       whoever uses this computer next. It is safe on the account. */
    if (!sync.on) {
      if (marker()) { marker(null); apply([]); }
      return;
    }
    if (!sync.url) return;

    var first = marker() !== String(sync.customer);

    fetch(sync.url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.ok || !Array.isArray(data.list)) return;
        var local = read();
        /* First visit by this customer on this device: fold in whatever they saved
           as a guest, so nothing disappears at sign-in. After that the account is
           the truth, so a removal made on their phone sticks here too. */
        var next = first ? newest([data.list, local]) : data.list;
        marker(sync.customer);
        if (JSON.stringify(next) !== JSON.stringify(local)) apply(next);
        if (JSON.stringify(next) !== JSON.stringify(data.list)) push();
      })
      .catch(function () {});
  }

  /* ---------- buttons ---------- */

  function paint(btn, saved) {
    btn.classList.toggle('is-saved', saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    var label = saved ? strings.saved : strings.save;
    var aria = saved ? strings.remove_aria : strings.save_aria;
    if (aria) btn.setAttribute('aria-label', aria);
    var text = btn.querySelector('.pj-wl__label');
    if (text && label) text.textContent = label;
  }

  function syncButtons(root) {
    var saved = {};
    read().forEach(function (i) { saved[i.h] = true; });
    (root || document).querySelectorAll('[data-pj-wl]').forEach(function (btn) {
      paint(btn, !!saved[btn.getAttribute('data-pj-wl-handle')]);
    });
  }

  function syncCount() {
    var n = read().length;
    document.querySelectorAll('[data-pj-wl-count]').forEach(function (el) {
      el.textContent = n > 9 ? '9+' : String(n);
      el.classList.toggle('is-active', n > 0);
    });
  }

  /* ---------- money ---------- */

  function money(cents) {
    var fmt = cfg.moneyFormat || '€{{amount_with_comma_separator}}';
    var amount = (cents / 100);
    function withComma(n, dec) {
      var parts = n.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return parts.length > 1 ? parts.join(',') : parts[0];
    }
    function withPeriod(n, dec) {
      var parts = n.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return withPeriod(amount, 2);
        case 'amount_no_decimals': return withPeriod(amount, 0);
        case 'amount_with_comma_separator': return withComma(amount, 2);
        case 'amount_no_decimals_with_comma_separator': return withComma(amount, 0);
        case 'amount_with_space_separator': return amount.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        default: return withPeriod(amount, 2);
      }
    });
  }

  /* ---------- the wishlist page ---------- */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function sized(url, w) {
    if (!url) return '';
    return url.replace(/(\.(?:jpg|jpeg|png|gif|webp))(\?|$)/i, '_' + w + 'x$1$2');
  }

  function card(p) {
    var price = (p.price_varies && p.price_min != null)
      ? (strings.from || 'From') + ' ' + money(p.price_min)
      : money(p.price);

    var img = p.featured_image
      ? '<img src="' + esc(sized(p.featured_image, 600)) + '" alt="' + esc(p.title) + '" loading="lazy" width="600" height="600">'
      : '';

    return ''
      + '<article class="pj-wl-item" data-pj-wl-row="' + esc(p.handle) + '">'
      +   '<div class="pj-wl-item__media">'
      +     '<a href="' + esc(p.url) + '" class="pj-wl-item__media" title="' + esc(p.title) + '">' + img + '</a>'
      +     '<button type="button" class="pj-wl pj-wl--card is-saved" data-pj-wl data-pj-wl-handle="' + esc(p.handle) + '" data-pj-wl-id="' + esc(p.id) + '" aria-pressed="true" aria-label="' + esc(strings.remove_aria || 'Remove from wishlist') + '">'
      +       '<span class="pj-wl__icon" aria-hidden="true">' + (cfg.icon || '') + '</span>'
      +     '</button>'
      +   '</div>'
      +   '<a class="pj-wl-item__title" href="' + esc(p.url) + '">' + esc(p.title) + '</a>'
      +   '<div class="pj-wl-item__price">' + price + '</div>'
      +   '<div class="pj-wl-item__actions">'
      +     '<a class="btn btn-primary" href="' + esc(p.url) + '">' + esc(strings.view || 'View') + '</a>'
      +   '</div>'
      + '</article>';
  }

  function renderPage() {
    var grid = document.querySelector('[data-pj-wl-grid]');
    if (!grid) return;

    var empty = document.querySelector('[data-pj-wl-empty]');
    var content = document.querySelector('[data-pj-wl-content]');
    var countEl = document.querySelector('[data-pj-wl-page-count]');
    var list = read();

    var loading = document.querySelector('[data-pj-wl-loading]');

    function show(which) {
      if (loading) loading.style.display = 'none';
      if (empty) empty.classList.toggle('is-shown', which === 'empty');
      if (content) content.classList.toggle('is-shown', which === 'content');
    }

    if (!list.length) { grid.innerHTML = ''; show('empty'); return; }

    Promise.all(list.map(function (i) {
      return fetch(cfg.rootUrl + 'products/' + encodeURIComponent(i.h) + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      var live = products.filter(Boolean);

      /* a product that no longer exists quietly leaves the list */
      if (live.length !== list.length) {
        var keep = {};
        live.forEach(function (p) { keep[p.handle] = true; });
        write(list.filter(function (i) { return keep[i.h]; }));
      }

      if (!live.length) { grid.innerHTML = ''; show('empty'); syncCount(); return; }

      grid.innerHTML = live.map(card).join('');
      if (countEl) countEl.textContent = live.length;
      show('content');
      syncCount();
    });
  }

  /* ---------- wiring ---------- */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-pj-wl]') : null;
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var handle = btn.getAttribute('data-pj-wl-handle');
    if (!handle) return;

    var onPage = !!btn.closest('[data-pj-wl-row]');
    var nowSaved = toggle(handle, btn.getAttribute('data-pj-wl-id'));

    if (onPage && !nowSaved) {
      var row = btn.closest('[data-pj-wl-row]');
      if (row) row.remove();
      if (!read().length) renderPage();
      var countEl = document.querySelector('[data-pj-wl-page-count]');
      if (countEl) countEl.textContent = read().length;
    }

    document.querySelectorAll('[data-pj-wl-handle="' + handle.replace(/"/g, '\\"') + '"]').forEach(function (b) {
      paint(b, nowSaved);
    });

    if (nowSaved) {
      btn.classList.remove('pj-wl--beat');
      void btn.offsetWidth;
      btn.classList.add('pj-wl--beat');
    }
  }, true);

  document.addEventListener('pj:wishlist:change', syncCount);

  function boot() {
    syncButtons();
    syncCount();
    renderPage();
    pull();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* quick-view and section rendering inject cards after load */
  var pending = null;
  new MutationObserver(function () {
    clearTimeout(pending);
    pending = setTimeout(function () { syncButtons(); }, 60);
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.PJWishlist = { read: read, has: has, toggle: toggle, remove: remove, count: function () { return read().length; } };
})();
